import { useStore } from '@shared/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogPortal, DialogTitle } from '../ui/DialogUI';
import { DialogClose } from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '@shared/utils/utils';
import ModalFooter from './ModalFooter';

export const modalVariants = cva(
    '',
    {
        variants: {
            type: {
                default: '',
                success: 'text-[#07bc0c]',
                error: 'text-[#e74c3c]',
                info: 'text-[#3498db]',
                warn: 'text-[#f1c40f]'
            },
            size: {
                'default': 'max-w-lg',
                'alert': 'max-w-lg',
                'xs': 'max-w-xs',
                'sm': 'max-w-sm',
                'md': 'max-w-md',
                'lg': 'max-w-lg',
                'xl': 'max-w-xl',
                '2xl': 'max-w-2xl',
                '3xl': 'max-w-3xl',
                '4xl': 'max-w-4xl',
                '5xl': 'max-w-5xl',
                '6xl': 'max-w-6xl',
                '7xl': 'max-w-7xl'
            }
        },
        defaultVariants: {
            type: 'default',
            size: 'default'
        }
    }
);

export default observer(function Modals() {
    // store
    const { modalStore: { instances, hideModal } } = useStore();

    return (
        <>
            {
                instances.map((modal, index) => {
                    return (
                        <Dialog
                            key={modal.id ?? index}
                            open={true}
                            onOpenChange={() => { }}
                        >
                            <DialogPortal>
                                <DialogContent
                                    className={cn('max-w-[90vw] bg-white p-4 md:p-6', modalVariants({ size: typeof modal.size === 'string' && !['default', 'alert', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl'].includes(modal.size) ? undefined : modal.size }), modal.contentClassName)}
                                    style={typeof modal.size === 'string' && !['default', 'alert', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl'].includes(modal.size) ? { maxWidth: modal.size } : undefined}
                                    onPointerDownOutside={() => {
                                        modal.closeWhenOutside && hideModal(modal.id);
                                    }}
                                >
                                    {
                                        modal.showHeader && (
                                            <DialogHeader>
                                                <DialogTitle
                                                    className={cn(
                                                        modalVariants({ type: modal.type }),
                                                        modal.titleClassName
                                                    )}
                                                >
                                                    {modal.title}
                                                </DialogTitle>
                                                <DialogDescription>
                                                    {modal.description}
                                                </DialogDescription>
                                                {modal.headerRender}
                                            </DialogHeader>
                                        )
                                    }
                                    {
                                        modal.showCloseIcon && (
                                            <DialogClose
                                                className='absolute right-[8px] top-[8px] z-[60] flex size-[26px] items-center justify-center rounded-full bg-[#333333] text-white shadow-md transition-transform hover:scale-110 focus:outline-none'
                                                onClick={() => hideModal(modal.id)}
                                            >
                                                <X className='h-5 w-5' />
                                                <span className='sr-only'>
                                                    Close
                                                </span>
                                            </DialogClose>
                                        )
                                    }
                                    {modal.content}
                                    {(modal.footer || modal.onSave || modal.onCancel) && (
                                        <DialogFooter className={cn('flex items-center justify-end gap-1', modal.footerClassName)}>
                                            {
                                                modal.footer ?
                                                    modal.footer :
                                                    (
                                                        <ModalFooter
                                                            cancelButton={modal.cancelButton}
                                                            cancelButtonClassName={modal.cancelButtonClassName}
                                                            onCancel={modal.onCancel}
                                                            saveButton={modal.saveButton}
                                                            saveButtonClassName={modal.saveButtonClassName}
                                                            onSave={modal.onSave}
                                                        />
                                                    )
                                            }
                                        </DialogFooter>
                                    )}
                                </DialogContent>
                            </DialogPortal>
                        </Dialog>
                    );
                })
            }
        </>
    );
});
